/* ============================================================
   SINGSPACE ADMIN - HOOK GESTION UTILISATEURS
   ============================================================ */

import { useState, useEffect } from 'react';
import { api } from '../services/api';

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER';
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
}

export interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isActive: boolean;
  createdAt: string;
  bookingsCount?: number;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function useAdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/admin');
      setUsers(response.data.data || []);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des administrateurs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const createUser = async (userData: Partial<AdminUser> & { password: string }) => {
    const response = await api.post('/users/admin', userData);
    await fetchUsers();
    return response.data.data;
  };

  const updateUser = async (id: string, userData: Partial<AdminUser>) => {
    const response = await api.put(`/users/admin/${id}`, userData);
    await fetchUsers();
    return response.data.data;
  };

  const deleteUser = async (id: string) => {
    await api.delete(`/users/admin/${id}`);
    await fetchUsers();
  };

  return {
    users,
    loading,
    error,
    createUser,
    updateUser,
    deleteUser,
    refresh: fetchUsers,
  };
}

export function useClients(page = 1, pageSize = 10, search = '') {
  const [data, setData] = useState<PaginatedResponse<Client>>({
    items: [],
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/clients', {
        params: { page, pageSize, search },
      });
      setData(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des clients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, pageSize, search]);

  return {
    ...data,
    loading,
    error,
    refresh: fetchClients,
  };
}

export function useClientDetails(clientId: string) {
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClient = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/users/clients/${clientId}`);
        setClient(response.data.data);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement du client');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchClient();
    }
  }, [clientId]);

  return { client, loading, error };
}
